import {
  EXPENSE_PAYMENT_ITEMS,
  formatMonthYearWithFy,
  formatUtilizationAmount,
  getSelectedExpensePaymentDetails,
  getSelectedTotalExpenseBreakdown,
  mapPrevExpenseDetails,
} from "./utilizationReportUtils";
import UtilizationAmountHover from "./UtilizationAmountHover";

const PopupRow = ({ label, value }) => {
  const amount = Number(value) || 0;
  if (amount === 0) return null;

  return (
    <div className="utilization-budget-info__popup-row">
      <span className="utilization-budget-info__popup-label">{label}</span>
      <span className="utilization-budget-info__popup-value">
        {formatUtilizationAmount(amount)}
      </span>
    </div>
  );
};

const ExpensePopupShell = ({ title, subtitle, children }) => (
  <div className="utilization-budget-info__popup utilization-budget-info__popup--prev">
    <div className="utilization-budget-info__popup-title">
      {title}
      <span className="utilization-budget-info__popup-subtitle">{subtitle}</span>
    </div>
    <div className="utilization-budget-info__popup-body">{children}</div>
  </div>
);

const ExpenseDetailsPopup = ({ subtitle, paymentDetails, totalExpense }) => (
  <ExpensePopupShell title="Computation of Expenses" subtitle={subtitle}>
    {EXPENSE_PAYMENT_ITEMS.map((item) => (
      <PopupRow
        key={item.key}
        label={item.label}
        value={paymentDetails[item.key]}
      />
    ))}
    <div className="utilization-budget-info__popup-divider" />
    <div className="utilization-budget-info__popup-row utilization-budget-info__popup-row--total">
      <span className="utilization-budget-info__popup-label">Net Expenses</span>
      <span className="utilization-budget-info__popup-value">
        {formatUtilizationAmount(totalExpense)}
      </span>
    </div>
  </ExpensePopupShell>
);

const getExpensePopupSubtitle = (expenseType, meta = {}) => {
  if (expenseType === "prevFy") {
    return `Upto previous FY ${meta.previousFyLabel || "-"}`;
  }

  if (expenseType === "selectedUpto") {
    return `Upto month - ${formatMonthYearWithFy(
      meta.previousMonthLabel,
      meta.selectedFyLabel
    )}`;
  }

  if (expenseType === "selectedForMonth") {
    return `For month - ${formatMonthYearWithFy(
      meta.selectedMonthLabel,
      meta.selectedFyLabel
    )}`;
  }

  return `For month - ${formatMonthYearWithFy(
    meta.selectedMonthLabel,
    meta.selectedFyLabel
  )}`;
};

const getExpensePaymentDetails = (expenseType, details = {}) => {
  if (expenseType === "selectedTotal") {
    const breakdown = getSelectedTotalExpenseBreakdown(details);
    return {
      vendor_payments: breakdown.totalVendorPayments,
      salary_payments: breakdown.totalSalaryPayments,
      dbt_payments: breakdown.totalDbtPayments,
    };
  }

  if (expenseType === "prevFy") {
    return mapPrevExpenseDetails(details);
  }

  return getSelectedExpensePaymentDetails(details, expenseType);
};

const EXPENSE_ARIA_LABELS = {
  prevFy: "View previous FY expense details",
  selectedUpto: "View selected FY expense upto details",
  selectedForMonth: "View selected FY expense for month details",
  selectedTotal: "View selected FY total expense details",
};

const UtilizationExpenseInfoIcon = ({
  expenseType,
  totalExpense,
  details,
  meta,
  children,
}) => {
  const displayValue = children ?? formatUtilizationAmount(totalExpense);
  const subtitle = getExpensePopupSubtitle(expenseType, meta);
  const paymentDetails = getExpensePaymentDetails(expenseType, details);

  return (
    <UtilizationAmountHover
      ariaLabel={EXPENSE_ARIA_LABELS[expenseType]}
      popup={
        <ExpenseDetailsPopup
          subtitle={subtitle}
          paymentDetails={paymentDetails}
          totalExpense={totalExpense}
        />
      }
    >
      {displayValue}
    </UtilizationAmountHover>
  );
};

export default UtilizationExpenseInfoIcon;
